import React, {PureComponent, ReactNode} from "react"
import {get} from "lodash"

import {connect} from "@irati/store"
import {H5} from "@irati/ui/src/Typography"
import {FormattedCurrency, FormattedMessage} from "@irati/utils/lang"
import id from "@irati/utils/id"
import {ReduxState} from "@irati/store/types"
import StatusType from "@irati/wallet/types/enums/Status"
import Spacing from "@irati/ui/src/Spacing"

import {Container, Img, ImgWrapper, Description, Fiscal, Amount, Status, SubTitle} from "./styled"

const getAmountElement = (amount: number, currency: string, status: StatusType): ReactNode | null => {
  const formattedCurrency = <FormattedCurrency isCents={false} value={amount} currency={currency} />
  const formattedCurrencyWithPlus = (<>
    <Spacing marginRight={0.5}>+</Spacing>
    {formattedCurrency}
  </>)

  switch (status) {
    case StatusType.PENDING:
      return <Amount pending>{formattedCurrencyWithPlus}</Amount>
    case StatusType.CANCELLED:
      return <Amount cancelled>{formattedCurrency}</Amount>
    case StatusType.FAILED:
      return <Amount failed>{formattedCurrency}</Amount>
    case StatusType.SPENT:
      return <Amount spent>{formattedCurrency}</Amount>
    case StatusType.SPENT_REFUND:
      return <Amount cleared>{formattedCurrencyWithPlus}</Amount>
    case StatusType.CLEARED:
    case StatusType.REWARD_WALLET_CLEARED:
      return <Amount cleared>{formattedCurrencyWithPlus}</Amount>
    case StatusType.REWARD_WALLET_SPENT:
      return <Amount spent>{formattedCurrency}</Amount>
    case StatusType.REFUND_SPENT:
      return <Amount spent>{formattedCurrency}</Amount>
    case StatusType.EXPIRED:
      return <Amount expired>{formattedCurrency}</Amount>
    default:
      return null
  }
}

const getStatusElement = (status: StatusType): ReactNode | null => {
  switch (status) {
    case StatusType.PENDING:
      return (
        <Status pending>
          <FormattedMessage id="wallet.primaryDetails.status.pending" />
        </Status>
      )
    case StatusType.CANCELLED:
      return (
        <Status cancelled>
          <FormattedMessage id="wallet.primaryDetails.status.cancelled" />
        </Status>
      )
    case StatusType.FAILED:
      return (
        <Status failed>
          <FormattedMessage id="wallet.primaryDetails.status.failed" />
        </Status>
      )
    case StatusType.SPENT:
      return (
        <Status spent>
          <FormattedMessage id="wallet.primaryDetails.status.spent" />
        </Status>
      )
    case StatusType.SPENT_REFUND:
      return (
        <Status>
          <FormattedMessage id="wallet.primaryDetails.status.spentRefund" />
        </Status>
      )
    case StatusType.CLEARED:
      return (
        <Status>
          <FormattedMessage id="wallet.primaryDetails.status.cleared" />
        </Status>
      )
    case StatusType.REFUND_SPENT:
      return (
        <Status>
          <FormattedMessage id="wallet.primaryDetails.status.refundSpent" />
        </Status>
      )
    case StatusType.EXPIRED:
      return (
        <Status expired>
          <FormattedMessage id="wallet.primaryDetails.status.expired" />
        </Status>
      )
    case StatusType.REWARD_WALLET_CLEARED:
      return (
        <Status>
          <FormattedMessage id="wallet.primaryDetails.status.rewardWalletCleared" />
        </Status>
      )
    case StatusType.REWARD_WALLET_SPENT:
      return (
        <Status>
          <FormattedMessage id="wallet.primaryDetails.status.rewardWalletSpent" />
        </Status>
      )
    default:
      return null
  }
}

interface Data {
  image: string,
  title: string | ReactNode,
  subTitle: string,
  savingsTitle?: string,
  amount: number,
  currency: string,
  status: StatusType,
  ProfilePicture?: ReactNode
}

interface Props {
  className?: string,
  imgHeightAuto?: boolean,
  isPhone: boolean,
  data: Data
}

class PrimaryDetails extends PureComponent<Props> {
  static defaultProps = {
    className: null,
    imgHeightAuto: false
  }

  render() {
    const {className, isPhone, data, imgHeightAuto} = this.props
    const {image, title, subTitle, savingsTitle, amount, currency, status, ProfilePicture} = data
    const idNamespace = id("primary-details", this.props)
    return (
      <Container className={className} id={idNamespace} alignItems="center">
        {!isPhone && (
          <ImgWrapper alignItems="center">{ProfilePicture || <Img autoHeight={imgHeightAuto} src={image} alt={title} />}</ImgWrapper>
        )}
        <Description>
          <H5>{title}</H5>
          <SubTitle>{subTitle}</SubTitle>
          {savingsTitle && <SubTitle>{savingsTitle}</SubTitle>}
        </Description>
        <Fiscal>
          {getAmountElement(amount, currency, status)}
          {getStatusElement(status)}
        </Fiscal>
      </Container>
    )
  }
}

const mapStateToProps = ({mq}: ReduxState) => ({
  isPhone: get(mq, "lessThan.medium", false)
})

export default connect(mapStateToProps)(PrimaryDetails)
